import {Injectable} from '@angular/core';

import {AuthService} from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class RoleService {
    constructor(private authService: AuthService) {
    }

  normalizeGroups(groups: string | string[] | null | undefined): string[] {
    if (Array.isArray(groups)) {
      return groups.filter(Boolean);
    }
    return groups ? [groups] : [];
  }

    /**
     * Platform superuser authorization (matches backend `user.is_superuser`).
     */
    isPlatformSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    /**
     * Login group label only. Use for display copy, not route/API authorization.
     */
    hasSuperuserGroup(groups: string | string[] | null | undefined): boolean {
    return this.normalizeGroups(groups).includes('Superuser');
  }

    /**
     * Platform superuser check. The groups argument is ignored; kept for call-site compatibility.
     */
    isSuperUser(_groups?: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser();
    }

  isAdmin(groups: string | string[] | null | undefined): boolean {
    return this.normalizeGroups(groups).includes('Admin');
  }

  isSalesManager(groups: string | string[] | null | undefined): boolean {
    return this.normalizeGroups(groups).includes('Sales Manager');
  }

    isMember(groups: string | string[] | null | undefined): boolean {
        return this.normalizeGroups(groups).includes('Member');
    }

  canAccessLeadManagement(groups: string | string[] | null | undefined): boolean {
      return this.isPlatformSuperuser() || this.isSalesManager(groups);
  }

  canAccessSubscriptions(groups: string | string[] | null | undefined): boolean {
      return this.isPlatformSuperuser() || this.isAdmin(groups) || this.isSalesManager(groups);
  }

  canAccessCoreOps(groups: string | string[] | null | undefined): boolean {
      return !this.isPlatformSuperuser() && !this.isSalesManager(groups);
  }

    /**
     * Org-scoped camera / bridge inventory (view).
     * Super User + Organization Admin/Member. Sales Manager and Demo User denied
     * (matches backend IsInfrastructureInventoryReader).
     * Mutations use canManageCameras / canManageBridges (Super User only).
     */
    canAccessCameraManagement(groups: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser()
            || this.isAdmin(groups)
            || this.isMember(groups);
    }

    /** Camera create / edit / delete — platform Super User only (P22-05). */
    canManageCameras(_groups?: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser();
    }

    /** Bridge list / detail view — same audience as camera inventory (P22-05). */
    canAccessBridgeManagement(groups: string | string[] | null | undefined): boolean {
        return this.canAccessCameraManagement(groups);
    }

    /** Bridge create / edit / delete / credentials — Super User only (P22-05). */
    canManageBridges(_groups?: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser();
    }

  canAccessAlertSettings(groups: string | string[] | null | undefined): boolean {
    return this.canAccessCoreOps(groups) && (this.isAdmin(groups) || this.normalizeGroups(groups).includes('Member'));
  }

    /**
     * Help & Support — customer-organization operators only (P22-05).
     * Admin, Member, and Demo User with an assigned organization.
     * Super User, Sales Manager, and non-customer roles denied.
     */
    canAccessHelpSupport(groups: string | string[] | null | undefined): boolean {
        if (this.isPlatformSuperuser() || this.isSalesManager(groups)) {
            return false;
        }
        if (!this.authService.hasCustomerOrganization()) {
            return false;
        }
        const normalized = this.normalizeGroups(groups);
        return this.isAdmin(normalized)
            || this.isMember(normalized)
            || normalized.includes('Demo User')
            || this.authService.isDemoUser();
    }

    /**
     * Vision Configuration management UI (P22-01) — platform Super User only.
     * Organization users retain Vision Context bootstrap via API, not this menu.
     */
    canAccessVisionConfiguration(_groups?: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser();
    }

    canEditVisionConfiguration(_groups?: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser();
    }

    /** EEN Device Health page: Super User + Organization Admin + Member (read). */
    canAccessDeviceHealth(groups: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser()
            || this.isAdmin(groups)
            || this.isMember(groups);
    }

    /**
     * Device Health History tab / history APIs (FE gate).
     * Backend IsDeviceHealthHistoryReader remains authoritative (Super User only).
     */
    canAccessDeviceHealthHistory(_groups?: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser();
    }

    /** Refresh Now: Super User + Organization Admin (Member remains read-only). */
    canRefreshDeviceHealth(groups: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser() || this.isAdmin(groups);
    }

    /** Platform diagnostics console: superuser only (Vision Operations + Provider Management). */
    canAccessVisionOperations(_groups?: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser();
    }

    canAccessOrganizationsListing(groups: string | string[] | null | undefined): boolean {
        return this.isPlatformSuperuser() || this.isSalesManager(groups);
    }

    organizationsListingQueryParams(activeOnly = false): { tab: string; active?: string } {
        return activeOnly ? {tab: 'customer', active: 'true'} : {tab: 'customer'};
    }

  showMainDashboard(groups: string | string[] | null | undefined): boolean {
    return true;
  }

  getDefaultRoute(groups: string | string[] | null | undefined): string {
    return '/dashboard';
  }
}
